import React, { useState, useRef, useEffect } from 'react';
import './HelpChatbot.css';
import { JURI_API_BASE, getAuthHeader } from '../../services/juriApi';

import SelectMenu from "../crm/SelectMenu";
const formatMessage = (text) => {
  if (!text) return null;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
};

const HelpChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: '1', role: 'assistant', text: 'Hi! I am the Jurident Platform Helper. How can I assist you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const [showSupportForm, setShowSupportForm] = useState(false);
  const [ticketFeature, setTicketFeature] = useState('');
  const [otherFeature, setOtherFeature] = useState('');
  const [ticketProblem, setTicketProblem] = useState('');
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);

  const [activeTab, setActiveTab] = useState('chat');
  const [myTickets, setMyTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [ticketToClose, setTicketToClose] = useState(null);

  const toggleChat = () => setIsOpen(!isOpen);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, showSupportForm]);

  const handleCloseTicket = async (ticketNumber) => {
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${JURI_API_BASE}/api/help-chat/tickets/${ticketNumber}/close`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        }
      });
      if (response.ok) {
        setTicketToClose(null);
        fetchMyTickets();
      }
    } catch (error) {
      console.error("Failed to close ticket:", error);
    }
  };

  const fetchMyTickets = async () => {
    setIsLoadingTickets(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${JURI_API_BASE}/api/help-chat/tickets`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMyTickets(data.tickets || []);
      }
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setIsLoadingTickets(false);
    }
  };

  useEffect(() => {
    if (isOpen || activeTab === 'tickets') {
      fetchMyTickets();
    }
  }, [isOpen, activeTab]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;

    const userMessage = { id: Date.now().toString(), role: 'user', text: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${JURI_API_BASE}/api/help-chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ query: userMessage.text })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let assistantMsgId = (Date.now() + 1).toString();
      setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', text: '' }]);

      setIsTyping(false);

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });

        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMsgId
              ? { ...msg, text: msg.text + chunk }
              : msg
          )
        );
      }
    } catch (error) {
      console.error('Error fetching chat response:', error);
      setIsTyping(false);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          text: 'I am sorry, I am having trouble connecting right now. Please contact support@jurident.com directly.'
        }
      ]);
    }
  };

  const handleTicketSubmit = async () => {
    let finalFeature = ticketFeature;
    if (ticketFeature === 'Others') {
      finalFeature = otherFeature;
    }
    
    if (!finalFeature.trim() || !ticketProblem.trim()) {
      alert("Please fill in both fields.");
      return;
    }
    
    setIsSubmittingTicket(true);
    try {
      const authHeader = await getAuthHeader();
      const response = await fetch(`${JURI_API_BASE}/api/help-chat/ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeader
        },
        body: JSON.stringify({ feature: ticketFeature, problem: ticketProblem })
      });
      
      if (!response.ok) throw new Error("Submission failed");
      
      const data = await response.json();
      const ticketNumber = data.ticket_number || 'Unknown';
      
      setShowSupportForm(false);
      setTicketFeature('');
      setTicketProblem('');
      fetchMyTickets();
      
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          text: `Your ticket (#${ticketNumber}) has been raised. Our staff will contact you at the earliest. Please feel free to ask more about the platform!`
        }
      ]);
    } catch (err) {
      console.error(err);
      setShowSupportForm(false);
      setMessages(prev => [
        ...prev,
        {
          id: Date.now().toString(),
          role: 'assistant',
          text: 'Sorry, I failed to create the alert. Please try again or email us directly at support@jurident.com.'
        }
      ]);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  return (
    <div className="help-chatbot-wrapper">
      <button
        className={`help-chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle Help Chat"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
          </svg>
        )}
      </button>

      <div className={`help-chatbot-container ${isOpen ? 'visible' : 'hidden'}`}>
        <div className="help-chatbot-header">
          <div className="header-info">
            <h3>Jurident Support</h3>
            <span className="status-indicator"></span>
            <span className="status-text">Online</span>
          </div>
        </div>

        <div className="help-chatbot-tabs">
          <button className={`chatbot-tab ${activeTab === 'chat' ? 'active' : ''}`} onClick={() => setActiveTab('chat')}>Chat</button>
          <button className={`chatbot-tab ${activeTab === 'tickets' ? 'active' : ''}`} onClick={() => setActiveTab('tickets')}>My Tickets</button>
        </div>

        {activeTab === 'tickets' ? (
          <div className="help-chatbot-tickets">
            {isLoadingTickets ? (
              <div className="tickets-message">Loading your tickets...</div>
            ) : myTickets.length === 0 ? (
              <div className="tickets-message">You have no support tickets.</div>
            ) : (
              <div className="tickets-list">
                {myTickets.map(t => (
                  <div key={t.ticket_number} className="ticket-card">
                    <div className="ticket-card-header">
                      <span className="ticket-id">{t.ticket_number}</span>
                      <span className={`ticket-status status-${t.status.toLowerCase().replace(' ', '-')}`}>{t.status}</span>
                    </div>
                    <div className="ticket-feature"><strong>Feature:</strong> {t.feature}</div>
                    <div className="ticket-problem"><strong>Problem:</strong> {t.problem}</div>
                    <div className="ticket-footer">
                      <div className="ticket-date">
                        {`${String(new Date(t.created_at).getDate()).padStart(2, '0')}/${String(new Date(t.created_at).getMonth() + 1).padStart(2, '0')}/${new Date(t.created_at).getFullYear()}`}
                      </div>
                      <div className="ticket-actions-small">
                        {t.status !== 'Closed' && t.status !== 'Resolved' && (
                          <button className="ticket-action-btn close-btn" onClick={() => setTicketToClose(t.ticket_number)}>Close</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : showSupportForm ? (
          <div className="support-ticket-form-container">
            <h4 style={{marginTop: 0, marginBottom: '15px', color: '#1f2937'}}>Create Support Alert</h4>
            <div className="ticket-field">
              <label>Which feature?</label>
              <SelectMenu
                value={ticketFeature}
                onChange={e => setTicketFeature(e.target.value)}
                disabled={isSubmittingTicket}
              >
                <option value="" disabled>Select a feature</option>
                <option value="All Cases">All Cases</option>
                <option value="Add Cases">Add Cases</option>
                <option value="Find Cases">Find Cases</option>
                <option value="Export Cause List">Export Cause List</option>
                <option value="My Teams">My Teams</option>
                <option value="Shared Cases">Shared Cases</option>
                <option value="Tasks & Events">Tasks & Events</option>
                <option value="Jurident Assistance">Jurident Assistance</option>
                <option value="My Files">My Files</option>
                <option value="Search Lawyer">Search Lawyer</option>
                <option value="Expenses">Expenses</option>
                <option value="Invoices">Invoices</option>
                <option value="Others">Others</option>
              </SelectMenu>
              {ticketFeature === 'Others' && (
                <input 
                  type="text" 
                  value={otherFeature} 
                  onChange={e => setOtherFeature(e.target.value)} 
                  placeholder="Please specify..."
                  disabled={isSubmittingTicket}
                  style={{ marginTop: '8px' }}
                />
              )}
            </div>
            <div className="ticket-field">
              <label>What is the problem?</label>
              <textarea 
                value={ticketProblem} 
                onChange={e => setTicketProblem(e.target.value)} 
                placeholder="Please describe the issue..."
                rows={4}
                disabled={isSubmittingTicket}
              />
            </div>
            <div className="ticket-actions">
              <button 
                className="ticket-submit-btn" 
                onClick={handleTicketSubmit} 
                disabled={isSubmittingTicket}
              >
                {isSubmittingTicket ? 'Submitting...' : 'Submit Alert'}
              </button>
              <button 
                className="ticket-cancel-btn" 
                onClick={() => setShowSupportForm(false)} 
                disabled={isSubmittingTicket}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="help-chatbot-messages">
              {messages.map((msg) => (
                <div key={msg.id} className={`message-bubble ${msg.role}`}>
                  <div className="message-text">{formatMessage(msg.text)}</div>
                  {msg.role === 'assistant' && msg.text.includes('support@jurident.com') && (
                    <div style={{ marginTop: '10px' }}>
                      <button 
                        onClick={() => setShowSupportForm(true)} 
                        className="contact-support-btn" 
                        title="Create Support Alert" 
                        style={{ display: 'inline-block', background: '#3b82f6', color: 'white', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '13px' }}
                      >
                        Contact Human Support
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="message-bubble assistant typing">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form className="help-chatbot-input-form" onSubmit={handleSend}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about features, pricing..."
                disabled={isTyping}
              />
              <button type="submit" disabled={!input.trim() || isTyping}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </button>
            </form>
          </>
        )}
      </div>

      {ticketToClose && (
        <div className="ticket-confirm-overlay">
          <div className="ticket-confirm-modal">
            <h4>Close Ticket?</h4>
            <p>Are you sure you want to permanently close and delete ticket {ticketToClose}?</p>
            <div className="ticket-confirm-actions">
              <button className="confirm-btn cancel" onClick={() => setTicketToClose(null)}>Cancel</button>
              <button className="confirm-btn yes" onClick={() => handleCloseTicket(ticketToClose)}>Yes, Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpChatbot;
